import { useEffect, useRef, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getDownloadJobsAction,
  cancelDownloadJobAction,
  addDownloadJobAction,
} from 'src/redux/actions/documentBundleActions';
import { documentBundleActions } from 'src/redux/actions/constant';
import documentBundleService from 'src/services/apiServices/document-bundle/document-bundle-service';
import { DownloadJob, JobStatus } from '../mock-data';

const POLL_INTERVAL_MS = 10_000;
const ACTIVE_STATUSES: JobStatus[] = ['QUEUED', 'PROCESSING', 'IN_PROGRESS'];

export function useDownloadJobs() {
  const dispatch = useDispatch();
  const { jobs, loading } = useSelector((state: any) => state.getDownloadJobsReducer);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [initialLoaded, setInitialLoaded] = useState(false);

  const fetchJobs = useCallback(() => {
    dispatch(getDownloadJobsAction());
  }, [dispatch]);

  // Silent poll — fetches in background, updates Redux without LOADING state
  const silentPoll = useCallback(async () => {
    try {
      const response = await documentBundleService.getDownloadJobs();
      if (response?.status === 200) {
        const mapped: DownloadJob[] = (response.data?.data ?? []).map((j: any) => ({
          job_id: j.jobId,
          job_name: j.jobName,
          status: j.status,
          progress_pct: j.progressPct ?? 0,
          created_at: j.createdAt,
          completed_at: j.completedAt,
          document_count: j.documentCount ?? 0,
          patient_count: j.patientCount ?? 0,
          size_bytes: j.sizeBytes,
          request_details: j.requestDetails,
        }));
        dispatch({ type: documentBundleActions.SUCCESS_GET_DOWNLOAD_JOBS, payload: mapped });
      }
    } catch {
      // Silent poll — don't surface errors
    }
  }, [dispatch]);

  // Initial load (shows skeleton)
  useEffect(() => {
    fetchJobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Track when first load completes
  useEffect(() => {
    if (!loading && !initialLoaded && jobs.length >= 0) {
      setInitialLoaded(true);
    }
  }, [loading, initialLoaded, jobs]);

  // Poll while active jobs exist — silent after first load
  useEffect(() => {
    if (loading) return;
    const hasActive = (jobs as DownloadJob[]).some((j) => ACTIVE_STATUSES.includes(j.status));
    if (!hasActive) return;
    timerRef.current = setTimeout(initialLoaded ? silentPoll : fetchJobs, POLL_INTERVAL_MS);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [jobs, loading, fetchJobs, silentPoll, initialLoaded]);

  const cancelJob = useCallback(
    async (jobId: string) => {
      try {
        await documentBundleService.cancelDownloadJob(jobId);
      } catch (e) {
        // Best-effort — API may fail but we still update UI optimistically
      }
      dispatch(cancelDownloadJobAction(jobId));
    },
    [dispatch]
  );

  const addJob = useCallback(
    (job: DownloadJob) => {
      dispatch(addDownloadJobAction(job));
    },
    [dispatch]
  );

  // Show loading skeleton only on first load
  const showLoading = loading && !initialLoaded;

  return { jobs: jobs as DownloadJob[], loading: showLoading, cancelJob, addJob };
}
